import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import { colorVariants, type ColorVariantsProps } from '../utils';

const countFillVariants = cva('fill-destructive', {
  variants: {
    variant: {
      foreground: 'fill-foreground',
      muted: 'fill-muted-foreground',
      primary: 'fill-primary',
      secondary: 'fill-secondary',
      accent: 'fill-accent',
      destructive: 'fill-destructive',
    },
  },
  defaultVariants: {
    variant: 'destructive',
  },
});

type CountVariant = NonNullable<
  VariantProps<typeof countFillVariants>['variant']
>;

export interface StackedCardsIconProps
  extends React.SVGProps<SVGSVGElement>, ColorVariantsProps {
  text: string;
  size?: number | string;
  textClassName?: string;
  countVariant?: CountVariant;
}

export const StackedCardsIcon = React.forwardRef<
  SVGSVGElement,
  StackedCardsIconProps
>(function StackedCardsIcon(
  {
    text,
    className,
    style,
    size,
    width,
    height,
    variant,
    textClassName,
    countVariant,
    ...rest
  },
  ref,
) {
  const computedWidth = width ?? size ?? 22;
  const computedHeight = height ?? size ?? 28;
  const colorClass = variant ? colorVariants({ variant }) : 'text-gray-600';
  const countFillClass = countFillVariants({ variant: countVariant });

  return (
    <svg
      ref={ref}
      width={computedWidth}
      height={computedHeight}
      viewBox="0 0 22 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(colorClass, className)}
      aria-hidden="true"
      focusable="false"
      style={{ ...(style as React.CSSProperties) }}
      {...rest}
    >
      <path
        d="M20.8157 6.53836C20.8157 4.31633 20.4054 3.06494 19.7334 2.33681C19.1506 1.70537 18.2797 1.36808 16.9956 1.23548L16.4202 1.19182H8.55454L7.97825 1.23645C6.69489 1.3695 5.82341 1.70803 5.24038 2.34069C4.56816 3.07035 4.15815 4.32199 4.15815 6.53836V18.4618C4.15819 20.438 4.41568 21.7246 5.12667 22.5392C5.82129 23.3348 7.08622 23.8268 9.51342 23.8268H15.4604C17.8879 23.8268 19.1535 23.335 19.8481 22.5392C20.559 21.7246 20.8156 20.4379 20.8157 18.4618V6.53836ZM22 18.4618C22 20.4596 21.7608 22.1536 20.7376 23.3261C19.6976 24.5176 17.9888 25.0194 15.4604 25.0194H9.51342C6.98518 25.0194 5.27715 24.5175 4.23717 23.3261C3.21375 22.1536 2.97477 20.4597 2.97473 18.4618V6.53836C2.97473 4.22411 3.39265 2.59337 4.37306 1.52949C5.35947 0.459237 6.79479 0.0921542 8.49093 0.000244141H16.4838C18.1789 0.0921328 19.6141 0.456449 20.6008 1.52561C21.5816 2.58849 22 4.21954 22 6.53836V18.4618Z"
        className="fill-current"
      />
      <path
        d="M13.477 3.57642C16.7776 3.75527 18.4329 4.97742 18.4329 9.51828V21.4418C18.4329 25.4163 17.4417 27.4035 12.4859 27.4035H6.53883C1.58297 27.4035 0.591797 25.4163 0.591797 21.4418V9.51828C0.591797 4.98736 2.24705 3.75527 5.54766 3.57642"
        className="fill-background"
      />
      <path
        d="M17.8409 9.51883C17.8409 7.2968 17.4306 6.04541 16.7587 5.31728C16.1759 4.68584 15.3049 4.34855 14.0208 4.21595L13.4455 4.17228H5.57981L5.00352 4.21692C3.72016 4.34997 2.84868 4.6885 2.26565 5.32116C1.59343 6.05082 1.18342 7.30245 1.18342 9.51883V21.4423C1.18346 23.4184 1.44095 24.7051 2.15194 25.5197C2.84656 26.3153 4.11149 26.8073 6.53869 26.8073H12.4857C14.9131 26.8073 16.1788 26.3154 16.8734 25.5197C17.5843 24.7051 17.8409 23.4183 17.8409 21.4423V9.51883ZM19.0253 21.4423C19.0253 23.4401 18.7861 25.1341 17.7629 26.3066C16.7229 27.4981 15.014 27.9998 12.4857 27.9998H6.53869C4.01045 27.9998 2.30241 27.498 1.26244 26.3066C0.239017 25.1341 3.75893e-05 23.4402 0 21.4423V9.51883C0 7.20457 0.417917 5.57384 1.39832 4.50996C2.38474 3.43971 3.82006 3.07262 5.5162 2.98071H13.5091C15.2042 3.0726 16.6393 3.43692 17.626 4.50608C18.6069 5.56896 19.0253 7.20001 19.0253 9.51883V21.4423Z"
        className="fill-current"
      />
      <text
        x="45%"
        y="62%"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize="15.29"
        fontWeight={700}
        className={cn(countFillClass, textClassName)}
      >
        {text}
      </text>
    </svg>
  );
});

StackedCardsIcon.displayName = 'StackedCardsIcon';
